import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Plane } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "@/context/LanguageContext";

export default function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [orbitSpeed, setOrbitSpeed] = useState("6s");
  const navigate = useNavigate();
  const loc = useLocation();

  // Speed up the orbiting plane briefly while the user scrolls —
  // gives the logo a subtle "alive" response to page interaction.
  useEffect(() => {
    let t;
    const onScroll = () => {
      setOrbitSpeed("1.8s");
      clearTimeout(t);
      t = setTimeout(() => setOrbitSpeed("6s"), 500);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  const link = (to, label, key) => {
    const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(`${to}/`));
    return (
      <Link
        key={key}
        to={to}
        onClick={() => setOpen(false)}
        data-testid={`nav-link-${key}`}
        className={`text-sm font-medium tracking-wide transition-colors ${
          active ? "text-[var(--gold)]" : "text-white/80 hover:text-[var(--gold)]"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-home-link" className="logo-ring">
            <img
              src="/logo.png"
              alt="Eazy-Travels"
              className="h-12 w-auto object-contain relative z-10"
            />
            {/* Airplane that orbits the globe section of the logo.
                The globe sits at ~12% horizontally; radius ~22px suits h-12. */}
            <span
              className="logo-orbit"
              style={{
                "--orbit-cx": "12%",
                "--orbit-radius": "22px",
                "--orbit-speed": orbitSpeed,
              }}
              aria-hidden="true"
            >
              <Plane
                size={12}
                strokeWidth={2.5}
                className="logo-orbit__plane"
              />
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {link("/", t("nav.home"), "home")}
            {link("/services", t("nav.services"), "services")}
            {link("/about", t("nav.about"), "about")}
            {link("/blog", t("nav.blog"), "blog")}
            {link("/contact", t("nav.contact"), "contact")}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => navigate("/contact")}
              data-testid="header-cta-consult"
              className="hidden md:inline-flex btn-gold text-sm"
            >
              {t("nav.consult")}
            </button>
            <button
              className="md:hidden text-white p-2"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-button"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden pb-5 flex flex-col gap-4 fade-in" data-testid="mobile-menu">
            {link("/", t("nav.home"), "home-mobile")}
            {link("/services", t("nav.services"), "services-mobile")}
            {link("/about", t("nav.about"), "about-mobile")}
            {link("/blog", t("nav.blog"), "blog-mobile")}
            {link("/contact", t("nav.contact"), "contact-mobile")}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/contact");
              }}
              data-testid="mobile-cta-consult"
              className="btn-gold text-sm w-fit"
            >
              {t("nav.consult")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
